/**
 * Shared pieces for the five brand login routes: the `redirect_url` search
 * contract the auth guards redirect with (Clerk's `<SignIn>` reads it from the
 * query string and prefers it over `fallbackRedirectUrl`), and the per-door
 * `<head>` so a login page carries its own title and is never indexed.
 *
 * Plain functions (no Effect, no React) so the eager route files stay tiny and
 * the behaviour is trivially unit-testable.
 *
 * @since 1.0.0
 */

/**
 * The login routes' search schema: an optional same-origin return-to.
 *
 * @since 1.0.0
 */
export interface LoginSearch {
  readonly redirect_url?: string;
}

/**
 * Keep `redirect_url` only when it is a string; anything else is dropped (the
 * guard already validated the value it redirected with — see
 * `isSafeReturnTo` in `server-auth.ts`; Clerk re-validates same-origin).
 *
 * @since 1.0.0
 */
export const validateLoginSearch = (search: Record<string, unknown>): LoginSearch => {
  const value = search["redirect_url"];
  return typeof value === "string" ? { redirect_url: value } : {};
};

/**
 * `<head>` for a login door: `Sign in — <brand>` and `noindex`.
 *
 * @since 1.0.0
 */
export const loginHead = (displayName: string) => ({
  meta: [
    { title: `Sign in — ${displayName}` },
    { name: "robots", content: "noindex" },
  ],
});

/**
 * `<head>` for a self-service sign-up door: `Create account — <brand>` and
 * `noindex`. Same shape as {@link loginHead} — the sign-up door is a sibling of
 * the login door, not an indexed marketing page.
 *
 * @since 1.0.0
 */
export const signUpHead = (displayName: string) => ({
  meta: [
    { title: `Create account — ${displayName}` },
    { name: "robots", content: "noindex" },
  ],
});
